import dayjs from 'dayjs';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { chartTimeframeToInterval } from 'utils/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';

export class LbankStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toLowerCase()}_${quote.toLowerCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://api.lbkex.com/v2/supplement/ticker/price.do');
      const params = new URLSearchParams({
        symbol: this.pair,
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        data: {
          price: string;
        }[];
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      let prec = 1;

      if (res.data) {
        prec = res.data.data[0].price.split('.')[1].length;
      }

      this.precision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Lbank ticker error: ', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const url = new URL('https://api.lbkex.com/v2/depth.do');
      const params = new URLSearchParams({
        symbol: this.pair,
        size: this.depth.toString(),
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{
        data: { asks: [string, string][]; bids: [string, string][] };
      }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      if (res.data) {
        const asks = res.data.data.asks;
        const bids = res.data.data.bids;

        this.asks = asks;
        this.bids = bids;

        const orderbook = {
          bids: bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]) as [
            string,
            string,
          ][],
          asks: asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]) as [
            string,
            string,
          ][],
        };

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('Lbank orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.lbank];

    try {
      const url = new URL('https://api.lbkex.com/v2/kline.do');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`,
        size: limit.toString(),
        type: innerInterval,
        time: Math.floor(
          Math.floor(Date.now() / 1000) -
            (limit - 1) * Math.floor(chartTimeframeToInterval(interval) / 1000),
        ).toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{ data: number[][] }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data.data ?? []).map(el => [
        el[0] * 1000,
        normalizeNumber(el[1]),
        normalizeNumber(el[2]),
        normalizeNumber(el[3]),
        normalizeNumber(el[4]),
        normalizeNumber(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Lbank klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.lbkex.com/v2/trades.do');
      const params = new URLSearchParams({
        symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`,
        size: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{
        data: {
          amount: string;
          price: string;
          type: string;
          date_ms: string;
        }[];
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (
        (result.data.data ?? []).map(item => ({
          price: item.price,
          quantity: item.amount,
          timestamp: item.date_ms,
          side: item.type.includes('buy') ? 'buy' : 'sell',
        })) ?? []
      );
    } catch (error) {
      console.log('Lbank trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();

    const intervalChannel = {
      60000: '1min',
      300000: '5min',
      900000: '15min',
      3600000: '1hr',
      14400000: '4hr',
      86400000: 'day',
      604800000: 'week',
    }[interval];

    const ws = new ReconnectingWebSocket('wss://www.lbkex.net/ws/V2/', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          action: 'subscribe',
          subscribe: 'depth',
          depth: this.depth.toString(),
          pair: this.pair,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          subscribe: 'kbar',
          kbar: intervalChannel,
          pair: this.pair,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          subscribe: 'tick',
          pair: this.pair,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          subscribe: 'trade',
          pair: this.pair,
        }),
      );
    };

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'depth') {
          const asks = data.depth.asks as [string, string][];
          const bids = data.depth.bids as [string, string][];

          this.bids = bids;
          this.asks = asks;
          this.onOrderbookUpdate({
            bids: bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]) as [
              string,
              string,
            ][],
            asks: asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]) as [
              string,
              string,
            ][],
          });
        }

        if (data.type === 'tick') {
          const { change, vol, turnover, low, latest, high } = data.tick;

          this.onTickerUpdate({
            baseVolume: normalizeNumber(vol),
            change: normalizeNumber(change),
            close: normalizeNumber(latest),
            high: normalizeNumber(high),
            last: latest.toFixed(this.precision),
            low: normalizeNumber(low),
            quoteVolume: normalizeNumber(turnover),
          });
        }

        if (data.type === 'kbar') {
          const { o, h, l, c, t, v } = data.kbar;

          this.klineUpdate([
            Math.floor(dayjs.utc(t).valueOf() / 1000),
            normalizeNumber(o),
            normalizeNumber(h),
            normalizeNumber(l),
            normalizeNumber(c),
            normalizeNumber(v),
          ]);
        }

        if (data.type === 'trade') {
          const { price, volume, direction, TS } = data.trade;

          this.onTradeUpdate({
            price,
            quantity: volume,
            timestamp: TS,
            side: direction.includes('buy') ? 'buy' : 'sell',
          });
        }

        if (data.ping) {
          this.ws?.send(
            JSON.stringify({
              action: 'pong',
              pong: data.ping,
            }),
          );
        }
      } catch (error) {
        console.log('Lbank socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
  };

  setOrderbookDepth(depth: number) {
    if (this.ws) {
      this.ws.send(
        JSON.stringify({
          action: 'unsubscribe',
          subscribe: 'depth',
          depth: this.depth.toString(),
          pair: this.pair,
        }),
      );
    }

    this.depth = depth;

    if (this.ws) {
      this.ws.send(
        JSON.stringify({
          action: 'subscribe',
          subscribe: 'depth',
          depth: this.depth.toString(),
          pair: this.pair,
        }),
      );
    }
  }

  switchOrderbookTick = () => {};

  getOrderbookTicks = (prec: number) => [
    {
      value: normalizeNumber(1 / 10 ** prec),
      tick: prec,
      id: prec,
    },
  ];

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new LbankStrategy(args);

    await instance.fetch();

    return instance;
  };
}
